import React, { useMemo, useState } from "react";
import { FaMapMarkerAlt, FaChevronRight } from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import { GoogleMap, Marker, useLoadScript } from "@react-google-maps/api";
import "./Location.css";

const CITIES = [
  { name: "Seattle", lat: 47.6062, lng: -122.3321 },
  { name: "Bellevue", lat: 47.6101, lng: -122.2015 },
  { name: "Kirkland", lat: 47.6815, lng: -122.2087 },
  { name: "Issaquah", lat: 47.5301, lng: -122.0326 },
  { name: "Lynnwood", lat: 47.8279, lng: -122.3053 },
  { name: "Factoria", lat: 47.5706, lng: -122.1536 },
  { name: "Bothell", lat: 47.7601, lng: -122.2054 },
  { name: "Brier", lat: 47.7845, lng: -122.2743 },
  { name: "Edmonds", lat: 47.8107, lng: -122.3776 },
  { name: "Mukilteo", lat: 47.9445, lng: -122.3046 },
  { name: "Renton", lat: 47.4829, lng: -122.2171 },
  { name: "Tacoma", lat: 47.2529, lng: -122.4443 },
  { name: "Medina", lat: 47.6209, lng: -122.2276 },
  { name: "Everett", lat: 47.9789, lng: -122.2021 },
  { name: "Auburn", lat: 47.3073, lng: -122.2285 },
  { name: "Federal Way", lat: 47.3223, lng: -122.3126 },
  { name: "Kent", lat: 47.3809, lng: -122.2348 },
  { name: "Kenmore", lat: 47.7573, lng: -122.244 },
  { name: "Tukwila", lat: 47.4626, lng: -122.255 },
  { name: "Mill Creek", lat: 47.8579, lng: -122.2043 },
  { name: "SeaTac", lat: 47.4447, lng: -122.3137 }, // corrected casing
  { name: "Burien", lat: 47.4698, lng: -122.3487 },
  { name: "Des Moines", lat: 47.4018, lng: -122.3243 },
  { name: "Normandy Park", lat: 47.4362, lng: -122.3407 },
  { name: "Mercer Island", lat: 47.5707, lng: -122.2221 },
  { name: "Snohomish", lat: 47.9126, lng: -122.0982 }
];

const mapContainerStyle = {
  width: "100%",
  height: "100%",
  borderRadius: "16px"
};

const defaultCenter = { lat: 47.5, lng: -122.2 };

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.05 } }
};

const itemAnimation = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

export default function Location() {
  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY
  });

  const [selectedCity, setSelectedCity] = useState(null);

  const mapOptions = useMemo(
    () => ({
      // merged styles here (you had two "styles" keys before; last one wins)
      styles: [
        // hide POIs & transit icons & some admin/water labels
        { featureType: "poi", stylers: [{ visibility: "off" }] },
        { featureType: "transit", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { featureType: "administrative", elementType: "geometry", stylers: [{ visibility: "off" }] },
        { featureType: "water", elementType: "labels.text", stylers: [{ visibility: "off" }] },

        // light road/landscape palette
        { featureType: "road", elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
        { featureType: "road", elementType: "labels.icon", stylers: [{ visibility: "off" }] },
        { featureType: "landscape", elementType: "geometry", stylers: [{ color: "#f8f9fa" }] }
      ],
      disableDefaultUI: true,
      zoomControl: true
    }),
    []
  );

  if (loadError) return <div>Error loading maps</div>;
  if (!isLoaded) return <div>Loading maps</div>;

  const handleCityClick = (city) => setSelectedCity(city);

  const handleCityKeyDown = (e, city) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setSelectedCity(city);
    }
  };

  return (
    <section className="location-section" id="locations" aria-labelledby="location-title">
      <div className="location-container">
        {/* Decorative elements */}
        <div className="location-decoration location-decoration-1" aria-hidden="true" />
        <div className="location-decoration location-decoration-2" aria-hidden="true" />
        <div className="location-decoration location-decoration-3" aria-hidden="true" />

        <motion.header
          className="location-header"
          initial="hidden"
          whileInView="visible"
          variants={fadeIn}
          viewport={{ once: true, margin: "-100px" }}
        >
          <div className="section-title-container">
            <div className="section-divider" />
            <h2 className="section-subtitle">SERVING THE PACIFIC NORTHWEST</h2>
            <div className="section-divider" />
          </div>
          <h1 className="location-title" id="location-title">
            Premium Carpet Cleaning <span>Service Areas</span>
          </h1>
          <p className="location-subtitle">
            We proudly serve the greater Seattle area with top-quality carpet cleaning services.
            Our professional team is ready to transform your carpets in these communities.
          </p>
        </motion.header>

        <div className="location-content">
          {/* Google Map Visualization */}
          <motion.div
            className="location-map"
            initial="hidden"
            whileInView="visible"
            variants={fadeIn}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ delay: 0.2 }}
          >
            <div className="map-wrapper">
              <GoogleMap
                mapContainerStyle={mapContainerStyle}
                zoom={10}
                center={selectedCity ? { lat: selectedCity.lat, lng: selectedCity.lng } : defaultCenter}
                options={mapOptions}
              >
                {CITIES.map((city) => (
                  <Marker
                    key={city.name}
                    position={{ lat: city.lat, lng: city.lng }}
                    icon={{
                      url: "https://maps.google.com/mapfiles/ms/icons/red-dot.png",
                      scaledSize: new window.google.maps.Size(32, 32)
                    }}
                    onClick={() => handleCityClick(city)}
                  />
                ))}
              </GoogleMap>

              <div className="map-overlay" aria-hidden="true" />
              <div className="map-label">
                <FaMapMarkerAlt className="map-pin-icon" aria-hidden="true" />
                <span>Seattle Metro Area</span>
              </div>
            </div>
          </motion.div>

          {/* Cities List */}
          <motion.div
            className="location-list-container"
            initial="hidden"
            whileInView="visible"
            variants={staggerContainer}
            viewport={{ once: true, margin: "-100px" }}
          >
            <div className="location-list-header">
              <h3 className="list-title">Cities We Serve</h3>
              <p className="list-subtitle">Click a city to show it on the map</p>
              <div className="list-divider" />
            </div>

            <motion.ul className="location-list" role="list">
              <AnimatePresence>
                {CITIES.map((city) => {
                  const active = selectedCity?.name === city.name;
                  return (
                    <motion.li
                      key={city.name}
                      className={`location-item ${active ? "active" : ""}`}
                      variants={itemAnimation}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      onClick={() => handleCityClick(city)}
                      onKeyDown={(e) => handleCityKeyDown(e, city)}
                      whileHover={{ y: -3 }}
                      layout
                      role="button"
                      tabIndex={0}
                      aria-pressed={active ? "true" : "false"}
                    >
                      <div className="item-content">
                        <FaMapMarkerAlt className="location-icon" aria-hidden="true" />
                        <span className="city-name">{city.name}</span>
                      </div>
                      <div className="item-hover-effect" aria-hidden="true" />
                      <FaChevronRight className="item-arrow" aria-hidden="true" />
                    </motion.li>
                  );
                })}
              </AnimatePresence>
            </motion.ul>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
